// NoteDialog.js
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { decode } from 'he';


const NoteDialog = ({ showNoteDialog, handleNoteClose, isNote, apiNoteUrl, apiGetNotesUrl, apiAuthKey, selectedDate, onCloseMenu  }) => {
  const [noteComment, setNoteComment] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noteResponseSubmitted, setNoteResponseSubmitted] = useState(false);  
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNoteForm, setShowNoteForm] = useState(!isNote);
  const [showNotes, setShowNotes] = useState(true);

  function sanitizeInput(input) {
    if (typeof input !== 'string') return input;

    return input
    .replace(/&/g, '&amp;')  // optional: preserve safe ampersands
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  }
  
  useEffect(() => {
    if (showNoteDialog) {
      setUsername('');
      setNoteComment('');
      setEmail('');
      setShowNoteForm(!isNote);
      setNoteResponseSubmitted(false);
      setNotes([]);
      
      if (isNote) {
        const fetchNotes = async () => {
          setLoading(true);
          try {
            const data = await getNotes(selectedDate);
            setNotes(data || []);
          } catch (error) {
            console.error('Error fetching notes:', error);
          } finally {
            setLoading(false);
          }
        };
  
        if (selectedDate) {
          fetchNotes();
        }
      }
    }
  }, [showNoteDialog, isNote, selectedDate]);
  
 
  const handleSanNote = (e) => {
    function isSafe(input) {
      const forbiddenPatterns = /<script.*?>.*?<\/script>|on\w+=".*?"|javascript:/gi;
      return !forbiddenPatterns.test(input);
    }
    
    const raw = e;
    if (!isSafe(raw)) {
      alert("Unsafe input detected!");
      return;
    }

    const cleanNote = sanitizeInput(raw);
    return(cleanNote);
  };

  useEffect(() => {
    if (showNoteDialog) {
      setUsername('');
      setNoteComment('');
      setEmail('');
      setShowNoteForm(!isNote);
      setShowNotes(true);
      setNoteResponseSubmitted(false);
      setNotes([]);
      if (isNote) {
        setLoading(true);
      }
      
    }
  }, [showNoteDialog, isNote]);
  

  const handleNoteSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return; // guard against double clicks

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!noteComment) {
      alert('Please enter a note.');
      return;
    }

    if (!username) {
      alert('Please enter your name.');
      return;
    }

    if (email && !emailRegex.test(email)) {
      alert('Please enter a valid email address or leave blank.');
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(`${apiNoteUrl}`, {
        method: 'POST',
        headers: {
          'Authorization': `${apiAuthKey}`
        },
        body: JSON.stringify({
          "reportDate": new Date().toISOString(),
          "username": sanitizeInput(username),
          "email": email,
          "issueDate": selectedDate,
          "note": handleSanNote(noteComment)
        })
      });
      setNoteResponseSubmitted(true);
      setTimeout(handleNoteClose, 4000);
      setTimeout(() => {
        setNoteResponseSubmitted(false);
       }, 4000);
      
    } catch (error) {
      console.error('Error posting note:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getNotes = async (selectedDate) => {
    try {
      const response = await fetch(`${apiGetNotesUrl}?id=${selectedDate}`, {
        method: 'GET',
        headers: { 'Authorization': `${apiAuthKey}` },
      });
      const data = await response.json();
      return data.notes;
    } catch (error) {
      console.error('Error fetching notes:', error);
      throw error;
    }
  }

  const decodeHtmlEntities = (text) => {
    if (typeof text !== 'string') return text; // safely return as-is

    // Quick check: does it contain any HTML entities?
    if (!/[&<>"']|&#?\w+;/.test(text)) {
      return text; // no entities detected — return as-is
    }
    text.innerHTML = text;
    return text.value;
  };

  return (
  showNoteDialog && (
    <div className="dialog-left-overlay notes">
      <div className="dialog">
        <div className="dialog-header">
          <button className="close-btn" onClick={handleNoteClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        {isNote && showNotes && (
          <>
            <h3>Visitor Notes</h3>
            {!showNoteForm && (
              <button
                className="add-note-btn"
                onClick={() => {
                  setShowNoteForm(true);
                  setShowNotes(false);
                }}
              >
                Add Your Note
              </button>
            )}
            {loading ? (
              <p>Loading notes...</p>
            ) : notes.length > 0 ? (
              <div>
                {notes.map((note, index) => (
                  <div key={index} className="note-container">
                    <p className="note-text">{decode(note.note)}</p>
                    <p className="note-author">— {note.username} ({format(new Date(note.reportDate), 'dd/MM/yyyy')})</p>
                  </div>
                ))}
              </div>
            ) : (
              <p>No notes found.</p>
            )}
          </>
        )}

        {showNoteForm &&
          (!noteResponseSubmitted ? (
            <form onSubmit={handleNoteSubmit}>
              <p className="prompt-text">
                Late schedule change? Notable entry? Misprint? Add a fun fact about today's listing.<br /><br />Emails are not published.
              </p>
              <textarea
                value={noteComment}
                onChange={(e) => setNoteComment(e.target.value)}
                placeholder="Enter your comment (max 255 chars)"
                maxLength="255"
              />
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your name (max 50 chars)"
                maxLength="50"
              />
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email (optional)"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="note-btn"
              >
                {isSubmitting && <span className="spinner" />}
                {isSubmitting ? '' : 'Add Note'}
              </button>
            </form>
          ) : (
            <p>Thank you for your note!</p>
          ))}
      </div>
    </div>
  )
  
)};

export default NoteDialog;
